/**
 * Entry chrome + modals for group detail / series wizard when a recipe group
 * filter is active (keeps ManufacturingModelsPanel under the file-size cap).
 */
import { useState } from "react";
import { ghostBtnClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingModelsComposeModal } from "./ManufacturingModelsComposeModal";
import { ManufacturingModelsGroupDetailModal } from "./ManufacturingModelsGroupDetailModal";
import { ManufacturingModelsSeriesWizardModal } from "./ManufacturingModelsSeriesWizardModal";
import type { MfgRecipeGroupSummary } from "@/lib/manufacturing/recipeGroupsClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

export function ManufacturingModelsGroupAuthoringChrome({
  isCreate,
  recipeGroupId,
  recipeGroups,
  recipes,
  canEditCode = false,
  onRecipesChanged,
  onPickRecipe,
  onOpenBomSandbox,
  onOpenPriceChange,
  onToast,
}: {
  isCreate: boolean;
  recipeGroupId: string;
  recipeGroups: ReadonlyArray<MfgRecipeGroupSummary>;
  recipes: readonly MfgRecipeSummary[];
  canEditCode?: boolean;
  onRecipesChanged: () => void | Promise<void>;
  onPickRecipe: (id: string) => void;
  onOpenBomSandbox: (groupId: string) => void;
  onOpenPriceChange?: () => void;
  onToast?: (message: string) => void;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [wizardGroupId, setWizardGroupId] = useState<string | undefined>(undefined);

  const selectedGroup =
    recipeGroupId && recipeGroupId !== "__ungrouped__"
      ? recipeGroups.find((g) => g.id === recipeGroupId) ?? null
      : null;

  if (!isCreate) return null;

  function openWizard(groupId?: string) {
    setWizardGroupId(groupId);
    setShowWizard(true);
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {selectedGroup ? (
          <>
            <button
              type="button"
              className={mfgSecondaryBtnClass}
              onClick={() => setShowDetail(true)}
            >
              Open group · {selectedGroup.name}
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => openWizard(selectedGroup.id)}
            >
              Series wizard
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => setShowCompose(true)}
            >
              Compose…
            </button>
            <button type="button" className={ghostBtnClass} onClick={() => onOpenPriceChange?.()}>
              Price change
            </button>
          </>
        ) : (
          <>
            <button type="button" className={ghostBtnClass} onClick={() => openWizard()}>
              Series wizard
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => setShowCompose(true)}
            >
              Compose…
            </button>
            <button type="button" className={ghostBtnClass} onClick={() => onOpenPriceChange?.()}>
              Price change
            </button>
          </>
        )}
      </div>

      {showDetail && selectedGroup ? (
        <ManufacturingModelsGroupDetailModal
          group={selectedGroup}
          recipes={recipes}
          canEditCode={canEditCode}
          onClose={() => setShowDetail(false)}
          onChanged={(msg) => {
            void onRecipesChanged();
            if (msg) onToast?.(msg);
          }}
          onOpenSeriesWizard={() => {
            setShowDetail(false);
            openWizard(selectedGroup.id);
          }}
          onAuthorGap={() => {
            setShowDetail(false);
            onOpenBomSandbox(selectedGroup.id);
          }}
          onPickRecipe={onPickRecipe}
        />
      ) : null}

      {showWizard ? (
        <ManufacturingModelsSeriesWizardModal
          recipes={recipes}
          initialGroupId={wizardGroupId}
          onClose={() => setShowWizard(false)}
          onDone={(msg) => {
            setShowWizard(false);
            void onRecipesChanged();
            onToast?.(msg);
          }}
        />
      ) : null}

      {showCompose ? (
        <ManufacturingModelsComposeModal
          recipes={recipes}
          initialGroupId={selectedGroup?.id ?? wizardGroupId}
          onClose={() => setShowCompose(false)}
          onRecipesRefresh={() => {
            void onRecipesChanged();
          }}
          onDone={(msg) => {
            setShowCompose(false);
            void onRecipesChanged();
            onToast?.(msg);
          }}
          onOpenBomSandbox={(groupId) => {
            setShowCompose(false);
            onOpenBomSandbox(groupId ?? selectedGroup?.id ?? "");
          }}
        />
      ) : null}
    </>
  );
}
